/**
 * @fileoverview Privacy Contract
 * @module contracts
 *
 * GDPR compliance and data privacy management.
 */

import { z } from 'zod';
import {
	createContract,
	ApiSuccessResponseSchema,
} from './contract.utils';

// ============================================================================
// ENUMS
// ============================================================================

export enum DataExportFormat {
	JSON = 'json',
	CSV = 'csv',
	PDF = 'pdf',
}

export enum DataDeletionStatus {
	PENDING = 'pending',
	IN_PROGRESS = 'in_progress',
	COMPLETED = 'completed',
	FAILED = 'failed',
}

// ============================================================================
// REQUEST SCHEMAS
// ============================================================================

export const ExportUserDataRequestSchema = z.object({
	userId: z.uuid(),
	format: z.enum(DataExportFormat).default(DataExportFormat.JSON),
	includeTestResults: z.boolean().default(true),
	includePayments: z.boolean().default(true),
	includeAnalyses: z.boolean().default(true),
});

export const DeleteUserDataRequestSchema = z.object({
	userId: z.uuid(),
	deleteTestResults: z.boolean().default(true),
	deletePayments: z.boolean().default(false),
	deleteAnalyses: z.boolean().default(true),
	deletePdfs: z.boolean().default(true),
	deleteEmails: z.boolean().default(false),
});

export const GetConsentRequestSchema = z.object({
	userId: z.uuid(),
});

export const UpdateConsentRequestSchema = z.object({
	userId: z.uuid(),
	marketingConsent: z.boolean().optional(),
	analyticsConsent: z.boolean().optional(),
	thirdPartyConsent: z.boolean().optional(),
});

// ============================================================================
// RESPONSE SCHEMAS
// ============================================================================

export const ExportUserDataResponseSchema = z.object({
	success: z.boolean(),
	downloadUrl: z.string().optional(),
	expiresAt: z.string().optional(),
	message: z.string(),
});

export const DeleteUserDataResponseSchema = z.object({
	success: z.boolean(),
	deletionId: z.string().optional(),
	status: z.enum(DataDeletionStatus),
	message: z.string(),
});

export const UserConsentSchema = z.object({
	userId: z.uuid(),
	marketingConsent: z.boolean(),
	analyticsConsent: z.boolean(),
	thirdPartyConsent: z.boolean(),
	consentDate: z.iso.datetime().describe('Consent timestamp (ISO 8601)'),
	updatedAt: z.iso.datetime().describe('Last update timestamp (ISO 8601)'),
});

export const DataDeletionRecordSchema = z.object({
	id: z.uuid(),
	userId: z.uuid(),
	status: z.enum(DataDeletionStatus),
	deletedTestResults: z.number(),
	deletedPayments: z.number(),
	deletedAnalyses: z.number(),
	deletedPdfs: z.number(),
	deletedEmails: z.number(),
	error: z.string().optional(),
	requestedAt: z.iso.datetime().describe('Request timestamp (ISO 8601)'),
	completedAt: z.iso.datetime().optional().describe('Completion timestamp (ISO 8601)'),
});

export const PrivacyPolicySchema = z.object({
	version: z.string(),
	status: z.enum(['active', 'draft', 'archived']),
	content: z.string(),
	effectiveDate: z.iso.datetime().describe('Effective date (ISO 8601)'),
	createdAt: z.iso.datetime().describe('Creation timestamp (ISO 8601)'),
	updatedAt: z.iso.datetime().describe('Last update timestamp (ISO 8601)'),
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type ExportUserDataRequest = z.infer<typeof ExportUserDataRequestSchema>;
export type DeleteUserDataRequest = z.infer<typeof DeleteUserDataRequestSchema>;
export type GetConsentRequest = z.infer<typeof GetConsentRequestSchema>;
export type UpdateConsentRequest = z.infer<typeof UpdateConsentRequestSchema>;

export type ExportUserDataResponse = z.infer<typeof ExportUserDataResponseSchema>;
export type DeleteUserDataResponse = z.infer<typeof DeleteUserDataResponseSchema>;
export type UserConsent = z.infer<typeof UserConsentSchema>;
export type DataDeletionRecord = z.infer<typeof DataDeletionRecordSchema>;
export type PrivacyPolicy = z.infer<typeof PrivacyPolicySchema>;

// ============================================================================
// API RESPONSE WRAPPERS
// ============================================================================

export const ExportUserDataApiResponseSchema = ApiSuccessResponseSchema(
	ExportUserDataResponseSchema,
);
export const DeleteUserDataApiResponseSchema = ApiSuccessResponseSchema(
	DeleteUserDataResponseSchema,
);
export const UserConsentApiResponseSchema = ApiSuccessResponseSchema(UserConsentSchema);
export const DataDeletionRecordApiResponseSchema =
	ApiSuccessResponseSchema(DataDeletionRecordSchema);
export const PrivacyPolicyApiResponseSchema = ApiSuccessResponseSchema(PrivacyPolicySchema);

// ============================================================================
// API CONTRACT
// ============================================================================

export const PRIVACY_API_CONTRACT = createContract('/privacy', {
	exportUserData: {
		method: 'POST' as const,
		path: '/export',
		body: ExportUserDataRequestSchema,
		response: ExportUserDataResponseSchema,
	},
	deleteUserData: {
		method: 'POST' as const,
		path: '/delete',
		body: DeleteUserDataRequestSchema,
		response: DeleteUserDataResponseSchema,
	},
	getConsent: {
		method: 'GET' as const,
		path: '/consent/:userId',
		params: GetConsentRequestSchema,
		response: UserConsentSchema,
	},
	updateConsent: {
		method: 'PUT' as const,
		path: '/consent',
		body: UpdateConsentRequestSchema,
		response: UserConsentSchema,
	},
	getDeletionStatus: {
		method: 'GET' as const,
		path: '/deletion/:deletionId',
		params: z.object({ deletionId: z.uuid() }),
		response: DataDeletionRecordSchema,
	},
	getCurrentPolicy: {
		method: 'GET' as const,
		path: '/policy/current',
		response: PrivacyPolicySchema,
	},
});

/** Route Constants for Controllers */
export const PRIVACY_ROUTES = {
	base: PRIVACY_API_CONTRACT.base,
	exportUserData: PRIVACY_API_CONTRACT.routes.exportUserData.path,
	deleteUserData: PRIVACY_API_CONTRACT.routes.deleteUserData.path,
	getConsent: PRIVACY_API_CONTRACT.routes.getConsent.path,
	updateConsent: PRIVACY_API_CONTRACT.routes.updateConsent.path,
	getDeletionStatus: PRIVACY_API_CONTRACT.routes.getDeletionStatus.path,
	getCurrentPolicy: PRIVACY_API_CONTRACT.routes.getCurrentPolicy.path,
};

export type PrivacyApiContract = typeof PRIVACY_API_CONTRACT;
